import React, {useEffect, useState} from "react"

import {Select} from "@irati/ui/src/FormItem"
import Validation from "@irati/utils/validation"
import id from "@irati/utils/id"
import {Region, RegionActiveState} from "@irati/types"
import {QueryRegionsArgs} from "@irati/types/ops"
import regionDataService from "@irati/region/services/regionData"
import {SelectOption} from "@irati/ui/src/FormItem/Select"

export const schema = (required = false) => Validation.string(required ? "This field is required" : undefined)

export type RegionsOnChange = (regionIds: string[], regions: Region[]) => any

export interface RegionsProps {
  id?: string,
  required?: boolean,
  onChange: RegionsOnChange,
  label?: string,
  hint?: string,
  /** Defaults true */
  isMulti?: boolean,
  isClearable?: boolean,
  isDisabled?: boolean,
  /**
   * Defaults to `Active`.
   * Set to `null` for all states.
   */
  regionState?: Region["state"],
  value?: string | string[],
  flush?: boolean
}

const Regions: React.FC<RegionsProps> = (props) => {
  const {
    onChange,
    label = "",
    hint = "",
    required = false,
    value,
    isClearable = false,
    isDisabled = false,
    isMulti = true,
    regionState = RegionActiveState.Active,
    flush
  } = props

  const [regions, setRegions] = useState<Region[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    ;(async() => {
      const request = async(initial: Region[], args: QueryRegionsArgs): Promise<void> => {
        const response = await regionDataService.list(args)
        const data = [...initial, ...response]
        const {cursor} = response.meta
        if (cursor) {
          return request(regions, cursor)
        }
        setRegions(data)
        setIsLoading(false)
      }

      setIsLoading(true)
      await request([], {limit: 200, state: regionState})
    })()
  }, [regionState])

  const handleChange = (raw?: string | SelectOption[]) => {
    const selectedRegionIds: string[] = []
    if (typeof raw === "string") {
      selectedRegionIds.push(raw)
    } else if (raw) {
      raw.map((t) => selectedRegionIds.push(t.value))
    }

    onChange(selectedRegionIds, regions.filter((t) => selectedRegionIds.includes(t.id)))
  }

  const valueToOption: any = () => {
    const selected = (isMulti ? value : [value]) as string[]
    return mapRegionsToOptions(regions.filter((t) => selected.includes(t.id)))
  }

  return (
    <Select
      isLoading={isLoading}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isMulti={isMulti}
      simpleValue
      name={props.id}
      id={id(props.id, props)}
      label={`${label}${required ? "*" : ""}`}
      placeholder="platform.fields.regions.placeholder"
      hint={hint}
      value={valueToOption()}
      onChange={handleChange}
      scrollMenuIntoView={false}
      validation={schema(required)}
      options={mapRegionsToOptions(regions)}
      flush={flush} />
  )
}

export default Regions

const mapRegionsToOptions = (regions: Region[]) =>
  regions
    .map((region: Region) => ({
      value: region.id,
      label: region.name
    }))
    .sort((a, b) => {
      if (a.label > b.label) return 1
      if (b.label > a.label) return -1
      return 0
    })
